/**
 * PointStyleDialog — the PTYPE / DDPTYPE point style dialog (Faz 7.3).
 * A simple checkbox/radio grid over the PDMODE bit field plus the PDSIZE
 * entry: base shape (dot/nothing/plus/x/tick) + circle + square overlays,
 * size > 0 absolute, 0 → 5% viewport, < 0 → viewport percent.
 */

import {
    Box,
    Button,
    Checkbox,
    Flex,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Radio,
    RadioGroup,
    Stack,
    Text,
} from '@chakra-ui/react';
import { useState } from 'react';
import { create } from 'zustand';
import { useTranslation } from 'react-i18next';
import { useSystemVarsStore } from '../../state/useSystemVarsStore';

interface PointStyleDialogState {
    open: boolean;
    setOpen: (open: boolean) => void;
}

export const usePointStyleDialogStore = create<PointStyleDialogState>((set) => ({
    open: false,
    setOpen: (open) => set({ open }),
}));

const SHAPES = [
    { value: 0, labelKey: 'dot' },
    { value: 1, labelKey: 'nothing' },
    { value: 2, labelKey: 'plus' },
    { value: 3, labelKey: 'cross' },
    { value: 4, labelKey: 'tick' },
] as const;

/** Typed i18n keys per shape (template literals miss the key union). */
const SHAPE_LABEL_KEYS = {
    dot: 'ui.cad.pointStyle.shape_dot',
    nothing: 'ui.cad.pointStyle.shape_nothing',
    plus: 'ui.cad.pointStyle.shape_plus',
    cross: 'ui.cad.pointStyle.shape_cross',
    tick: 'ui.cad.pointStyle.shape_tick',
} as const;

export function PointStyleDialog() {
    const { t } = useTranslation();
    const open = usePointStyleDialogStore((s) => s.open);
    const setOpen = usePointStyleDialogStore((s) => s.setOpen);
    const pdmode = useSystemVarsStore((s) => s.PDMODE);
    const pdsize = useSystemVarsStore((s) => s.PDSIZE);
    const [draftShape, setDraftShape] = useState<number>(pdmode & 15);
    const [draftCircle, setDraftCircle] = useState<boolean>((pdmode & 32) !== 0);
    const [draftSquare, setDraftSquare] = useState<boolean>((pdmode & 64) !== 0);
    const [draftSize, setDraftSize] = useState<string>(String(pdsize));

    const apply = () => {
        const mode = (draftShape & 15) | (draftCircle ? 32 : 0) | (draftSquare ? 64 : 0);
        const size = Number(draftSize);
        const store = useSystemVarsStore.getState();
        store.set('PDMODE', mode);
        store.set('PDSIZE', Number.isFinite(size) ? size : 0);
        setOpen(false);
    };

    return (
        <Modal isOpen={open} onClose={() => setOpen(false)} size="sm">
            <ModalOverlay />
            <ModalContent bg="bg.panel">
                <ModalHeader fontSize="sm">{t('ui.cad.pointStyle.title')}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Stack spacing={4}>
                        <Box>
                            <Text fontSize="10px" color="text.secondary" textTransform="uppercase" mb={1}>
                                {t('ui.cad.pointStyle.shape')}
                            </Text>
                            <RadioGroup
                                value={String(draftShape)}
                                onChange={(v) => setDraftShape(Number(v))}
                            >
                                <Stack spacing={1}>
                                    {SHAPES.map((s) => (
                                        <Radio key={s.value} value={String(s.value)} size="sm" colorScheme="blue">
                                            <Text fontSize="xs" color="text.primary">
                                                {t(SHAPE_LABEL_KEYS[s.labelKey])}
                                            </Text>
                                        </Radio>
                                    ))}
                                </Stack>
                            </RadioGroup>
                        </Box>
                        <Stack spacing={1}>
                            <Checkbox
                                size="sm"
                                colorScheme="blue"
                                isChecked={draftCircle}
                                onChange={(e) => setDraftCircle(e.target.checked)}
                            >
                                <Text fontSize="xs" color="text.primary">{t('ui.cad.pointStyle.circle')}</Text>
                            </Checkbox>
                            <Checkbox
                                size="sm"
                                colorScheme="blue"
                                isChecked={draftSquare}
                                onChange={(e) => setDraftSquare(e.target.checked)}
                            >
                                <Text fontSize="xs" color="text.primary">{t('ui.cad.pointStyle.square')}</Text>
                            </Checkbox>
                        </Stack>
                        <Flex align="center" gap={2}>
                            <Text fontSize="xs" color="text.primary" whiteSpace="nowrap" flexShrink={0}>
                                {t('ui.cad.pointStyle.size')}
                            </Text>
                            <Input
                                size="sm"
                                variant="filled"
                                bg="surface.300"
                                color="text.primary"
                                value={draftSize}
                                onChange={(e) => setDraftSize(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') apply(); }}
                            />
                        </Flex>
                        <Text fontSize="10px" color="text.secondary">
                            {t('ui.cad.pointStyle.sizeHint')}
                        </Text>
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button size="sm" variant="ghost" mr={2} onClick={() => setOpen(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button size="sm" colorScheme="blue" onClick={apply}>
                        {t('common.ok')}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
